import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";

interface TimeEntry {
  startTime: string;
  endTime: string;
  duration: number; // minutes
  costCode: string;
  notes: string;
}

interface LaborTimeData {
  employeeId: string;
  jobId: string;
  workDate: string;
  shift: string;
  timeEntries: TimeEntry[];
  totalHours: number;
  overtimeHours: number;
  status: "draft" | "submitted" | "approved" | "rejected";
  approvedBy?: string;
  approvalDate?: string;
  comments?: string;
}

interface CostCodeSummary {
  costCode: string;
  description: string;
  minutes: number;
  notes: string;
}

class LaborTimeTrackingWizardStore {
  rootStore: RootStore;

  // UI State
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Form Data
  formData: LaborTimeData = {
    employeeId: "",
    jobId: "",
    workDate: new Date().toISOString().split("T")[0],
    shift: "day",
    timeEntries: [],
    totalHours: 0,
    overtimeHours: 0,
    status: "draft",
  };

  // Lookup Data
  costCodes: any[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  };

  updateFormData = <K extends keyof LaborTimeData>(
    field: K,
    value: LaborTimeData[K]
  ) => {
    this.formData[field] = value;
  };

  addTimeEntry = (entry: TimeEntry) => {
    this.formData.timeEntries.push(entry);
    this.calculateTotalHours();
  };

  removeTimeEntry = (index: number) => {
    this.formData.timeEntries.splice(index, 1);
    this.calculateTotalHours();
  };

  updateTimeEntry = (index: number, entry: TimeEntry) => {
    this.formData.timeEntries[index] = entry;
  };

  calculateTotalHours = () => {
    const totalMinutes = this.formData.timeEntries.reduce(
      (sum, entry) => sum + entry.duration,
      0
    );
    const totalHours = totalMinutes / 60;
    this.formData.totalHours = totalHours;

    // Calculate overtime (over 8 hours)
    if (totalHours > 8) {
      this.formData.overtimeHours = totalHours - 8;
    } else {
      this.formData.overtimeHours = 0;
    }
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.error = null;

    switch (this.activeStep) {
      case 0:
        if (!this.formData.employeeId) {
          this.error = "Please select an employee";
          return false;
        }
        if (!this.formData.jobId) {
          this.error = "Please select a job/project";
          return false;
        }
        if (!this.formData.workDate) {
          this.error = "Please select a work date";
          return false;
        }
        return true;

      case 1:
        if (this.formData.timeEntries.length === 0) {
          this.error = "Please add at least one time entry";
          return false;
        }
        return true;

      case 2: {
        const missingCostCodes = this.formData.timeEntries.some(
          (entry) => !entry.costCode
        );
        if (missingCostCodes) {
          this.error = "Please assign cost codes to all time entries";
          return false;
        }
        return true;
      }

      case 3:
        return true;

      default:
        return true;
    }
  };

  // API Actions
  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      // Load cost codes
      if (this.rootStore.costCodeStore) {
        await this.rootStore.costCodeStore.find({
          query: {
            $limit: 100,
            active: true,
          },
        });
      }

      // Load users if needed
      if (this.rootStore.userStore) {
        await this.rootStore.userStore.find({
          query: {
            $limit: 100,
            active: true,
          },
        });
      }

      // Load jobs if needed
      if (this.rootStore.jobStore) {
        await this.rootStore.jobStore.find({
          query: {
            $limit: 100,
            status: "active",
          },
        });
      }

      runInAction(() => {
        this.costCodes = this.rootStore.costCodeStore?.entities || [];
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load initial data";
        this.loading = false;
      });
    }
  };

  submitTimesheet = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      // Create timesheet record
      const timesheetData = {
        ...this.formData,
        submittedAt: new Date().toISOString(),
        submittedBy: this.rootStore.authStore?.user?.id,
      };

      // API call would go here
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulated API call

      runInAction(() => {
        this.success = "Timesheet submitted successfully!";
        this.loading = false;

        // Reset form after successful submission
        setTimeout(() => {
          this.reset();
          this.rootStore.routerStore.goTo("Dashboard");
        }, 2000);
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to submit timesheet";
        this.loading = false;
      });
    }
  };

  // Computed Values
  get users() {
    return this.rootStore.userStore?.entities || [];
  }

  get jobs() {
    return this.rootStore.jobStore?.entities || [];
  }

  get selectedEmployee() {
    return this.users.find((u) => u.id === this.formData.employeeId);
  }

  get selectedJob() {
    return this.jobs.find((j) => j.id === this.formData.jobId);
  }

  get totalMinutes() {
    return this.formData.timeEntries.reduce(
      (sum, entry) => sum + entry.duration,
      0
    );
  }

  get costCodeSummary(): CostCodeSummary[] {
    const summary = new Map<string, CostCodeSummary>();

    this.formData.timeEntries.forEach((entry) => {
      if (entry.costCode) {
        const existing = summary.get(entry.costCode);
        if (existing) {
          existing.minutes += entry.duration;
          if (entry.notes) {
            existing.notes = existing.notes
              ? `${existing.notes}; ${entry.notes}`
              : entry.notes;
          }
        } else {
          const costCode = this.costCodes.find(
            (c) => c.code === entry.costCode
          );
          summary.set(entry.costCode, {
            costCode: entry.costCode,
            description: costCode?.description || "",
            minutes: entry.duration,
            notes: entry.notes,
          });
        }
      }
    });

    return Array.from(summary.values());
  }

  // Reset
  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.formData = {
      employeeId: "",
      jobId: "",
      workDate: new Date().toISOString().split("T")[0],
      shift: "day",
      timeEntries: [],
      totalHours: 0,
      overtimeHours: 0,
      status: "draft",
    };
  };
}

export default LaborTimeTrackingWizardStore;